import { ThinClient } from 'common';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { browserName, isMobile } from 'react-device-detect';
import { Capability, InternalCapability } from '../data/account';
import { Carrier } from '../data/carriers';
import { PrintOnEnum } from '../data/printOn';
import { Service, ServiceType } from '../data/services';
import PluginInterface from '../naws/PluginInterface';
import Session from './Session';

class PrintUtil {
	static isDomesticCountryCode(countryCode) {
		return countryCode.toUpperCase().search(/AS|GU|MH|MP|PW|PR|VI|US/) !== -1;
	}

	static isPostalSupportedService(servicePackage) {
		const supportedServices = [
			Service.UspsFirstClassMailIntlLargeEnvelope,
			Service.UspsPriorityMailIntlPackage,
			Service.UspsPriorityMailExpressIntlPackage,
		];

		return supportedServices.includes(servicePackage);
	}

	static isPostalService(selectedPrint) {
		const {
			printDetails: {
				toAddress: { countryCode },
				serviceType,
				packagingType,
			},
		} = selectedPrint;
		const postalOverride = Session.hasCapability(Capability.PostalOverride);
		const isInternational = !this.isDomesticCountryCode(countryCode);
		const isPostalSupportedService = this.isPostalSupportedService(
			`${serviceType}_${packagingType}`
		);

		return postalOverride && isInternational && isPostalSupportedService;
	}

	static isAPO(state) {
		return state.search(/AA|AE|AP|AS|FM|GU|MH|MP|PW/) !== -1;
	}

	/**
	 * Customs is required when:
	 * 1) the address is an international address
	 * 2) the address is not international, but it's an apo/fpo address.
	 */
	static isCustomsRequired(selectedPrint) {
		const {
			printDetails: {
				toAddress: { countryCode, stateProvince },
			},
		} = selectedPrint;
		const isInternational = !this.isDomesticCountryCode(countryCode);

		if (
			isInternational ||
			(stateProvince !== '' && this.isAPO(stateProvince))
		) {
			return true;
		}

		return false;
	}

	/**
	 * Check if the print(s) will be printed using PDF engine. To be eligible for PDF printing, all orders
	 * need to meet the following criteria:
	 * 1) media must be either 'Roll - 4" x 6"' or 'Roll - 4 1/8" x 6 1/4"', and
	 * 2) print type must be one of the following:
	 *		- Shipping Labels with no customs forms, or
	 *		- GAP Single Piece, or
	 *		- FCI Large Envelope Flat - Non Document
	 *		- carrier is DHL
	 * 3) platform is not either mobile or tablet.
	 * @param {array} selectedPrints An array of selected orders for print.
	 * @param {string} printOnValue Print On media selected by the user
	 * @return {boolean}
	 */
	static isEligibleForPdfPrint(
		selectedPrints = [],
		printOnValue = '',
		reprintData = {}
	) {
		const isDesktop = !isMobile;

		if (
			isDesktop &&
			isArray(selectedPrints) &&
			!this.isPluginPrintFlow(reprintData)
		) {
			for (let i = 0; i < selectedPrints.length; i++) {
				const selectedPrint = selectedPrints[i];
				const meetsMediaRequirements =
					printOnValue === PrintOnEnum.Roll4x6 ||
					printOnValue === PrintOnEnum.Roll418x614;
				const isDhl =
					selectedPrints?.[i]?.printDetails?.carrier === Carrier.DHLExpress;

				if (
					!meetsMediaRequirements ||
					(!(
						this.isEligibleForGapSinglePieceOrFCIFlatNonDoc([selectedPrint]) ||
						this.isPostalService(selectedPrint) ||
						this.isPostalPrint(selectedPrint)
					) &&
						this.isCustomsRequired(selectedPrint) &&
						!isDhl)
				) {
					return false;
				}
			}
		} else {
			return false;
		}

		return true;
	}

	static isAllowEnvelopeGifPrinting() {
		const { isAllowEnvelopeGif } = Session.getPreferences();

		return isAllowEnvelopeGif === '1' && browserName === 'Chrome';
	}

	static isPluginReady() {
		return (
			PluginInterface.status === 'available' && PluginInterface.hasPrinters
		);
	}

	static isPluginRequired(selectedPrints) {
		const selectedPrint = Array.isArray(selectedPrints)
			? selectedPrints[0]
			: selectedPrints;
		const { printLayout = '', printOn = '' } = selectedPrint || {};
		const isNetstampsRoll = printLayout.includes('RollTypeSorD');
		const isThermal = printOn.includes('roll');
		const isEnvelope =
			printOn.includes('Envelope') && !this.isAllowEnvelopeGifPrinting();

		if (isNetstampsRoll || isEnvelope) {
			return true;
		} else if (
			isThermal &&
			!this.isEligibleForPdfPrint(selectedPrints, printOn)
		) {
			return true;
		}

		return false;
	}

	static isPluginPrintFlow(selectedPrints) {
		return (
			!ThinClient.isInThinClient() &&
			this.isPluginReady() &&
			this.isPluginRequired(selectedPrints)
		);
	}

	/**
	 * Checks if the selected prints are eligible for the following GAP label criteria:
	 * GAP single piece label - see isPresortOther() for more details,
	 * or
	 * FCI LargeEnvelope Flat - Non Document - see isPresortFCIFlat() for more details.
	 */
	static isEligibleForGapSinglePieceOrFCIFlatNonDoc(selectedPrints = []) {
		return (
			this.isPresortOther(selectedPrints) ||
			this.isPresortFCIFlat(selectedPrints)
		);
	}

	// checks for presort capabilities for FCI, PMI, or PMEI services and if it is GAP single piece
	static isPresortOther(seletedPrints = []) {
		const postalOverride = Session.hasCapability(Capability.PostalOverride);
		const canPrintFCIPresort = Session.hasCapability(InternalCapability.FciGap);
		const canPrintPMIPresort = Session.hasCapability(InternalCapability.PmiGap);
		const canPrintPMEIPresort = Session.hasCapability(
			InternalCapability.PmeiGap
		);
		const canPrintIntlPresortSinglePiece = Session.hasCapability(
			Capability.IntlGapSinglePiece
		);

		if (!postalOverride) {
			for (let i = 0; i < seletedPrints.length; i++) {
				const { serviceType } = seletedPrints?.[i]?.printDetails;

				if (
					canPrintIntlPresortSinglePiece &&
					((serviceType === ServiceType.UspsFirstClassMailIntl &&
						canPrintFCIPresort) ||
						(serviceType === ServiceType.UspsPriorityMailIntl &&
							canPrintPMIPresort) ||
						(serviceType === ServiceType.UspsPriorityMailExpressIntl &&
							canPrintPMEIPresort))
				) {
					return true;
				}
			}
		}

		return false;
	}

	// check if user is printing orders using FCI Flats and custom contents not equal to documents
	// and then check for the presort capabilites as only users with those certain capabilties enabled will see domestic labels for international prints
	static isPresortFCIFlat(selectedPrints = []) {
		const canPrintFCIPresort = Session.hasCapability(InternalCapability.FciGap);
		const isSasAccount =
			Session.getAccountInfo()?.accountInfo?.accountId !== 0 || false;

		for (let i = 0; i < selectedPrints.length; i++) {
			const { service, customsContents } = selectedPrints?.[i]?.printDetails;
			const isFciFlatNonDoc =
				service === Service.UspsFirstClassMailIntlLargeEnvelope &&
				!customsContents.includes('Documents');

			if (
				isSasAccount &&
				canPrintFCIPresort &&
				isFciFlatNonDoc &&
				!this.isPostalPrint(selectedPrints[i])
			) {
				return true;
			}
		}

		return false;
	}

	static isPostalPrint(selectedPrint) {
		const { isGap, isContainerLabel } = selectedPrint?.printDetails;

		return !isGap && !isContainerLabel;
	}

	static getPrintMarginsForThermalLabels(printerName, printMedia) {
		let offsets = {
			cWidthOffset: 0,
			cHeightOffset: 0,
			drawX: 0,
			drawY: 0,
		};

		if (!isEmpty(printerName)) {
			if (printerName.search(/TSC ME/) !== -1) {
				if (printMedia === PrintOnEnum.Roll4x6) {
					offsets = {
						// offsets that will be added in the image process for desired canvas width
						cWidthOffset: 38,
						cHeightOffset: 0,

						// desired coordinates from where the label should be drawn on the canvas
						drawX: 25,
						drawY: 0,
					};
				} else if (printMedia === PrintOnEnum.Roll418x614) {
					offsets = {
						cWidthOffset: 38,
						cHeightOffset: 0,

						drawX: 20,
						drawY: 0,
					};
				}
			}
		}

		return offsets;
	}
}

export default PrintUtil;
